import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { ScrollText, ExternalLink } from "lucide-react";
import StepLayout from "./StepLayout";

const LINKS = {
  terms: "https://vanguardbeacon.com/terms",
  privacy: "https://vanguardbeacon.com/privacy",
  aiDisclaimer: "https://vanguardbeacon.com/ai-disclaimer",
};

/**
 * Step 0 — Welcome to Vanguard Beacon.
 * Owner must read and agree to the Terms & Conditions, Privacy Policy and
 * AI Disclaimer before setting up their company profile. Acceptance is
 * recorded server-side (who + when + version) for audit/legal proof.
 */
export default function Step0Terms({ stepIndex, totalSteps, progress, onAdvance }) {
  const [agreed, setAgreed] = useState(!!progress?.terms);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!agreed) return setErr("Please tick the box to agree before continuing.");
    if (progress?.terms) return onAdvance({ terms: true });
    setBusy(true);
    try {
      await api.post("/tenant/onboarding/accept-terms");
      await onAdvance({ terms: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Could not record your agreement. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Welcome to Vanguard Beacon"
      subtitle="Before you create your company profile, please review and agree to our terms."
      description={
        <div className="space-y-3 text-base">
          <p className="m-0">
            Vanguard Beacon is a software platform designed to assist organizations in managing
            compliance activities. It does not replace professional judgement, regulatory
            requirements, or certification processes.
          </p>
          <p className="m-0">
            AI-generated insights and recommendations should always be reviewed by authorized
            personnel before implementation.
          </p>
        </div>
      }
      onNext={submit}
      nextLabel="Agree & continue"
      nextDisabled={!agreed}
      busy={busy}
      errorMsg={err}
    >
      <div className="vb-card p-6 space-y-5" data-testid="terms-panel">
        <div className="flex items-center gap-2">
          <ScrollText size={18} style={{ color: "var(--vb-text-2)" }} />
          <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Terms of use</h2>
        </div>

        <p className="text-sm leading-relaxed opacity-80 m-0">
          By creating an account, you agree to our Terms &amp; Conditions and acknowledge that you
          have read our Privacy Policy.
        </p>
        <p className="text-xs leading-relaxed opacity-60 m-0">
          This agreement is recorded against your account for audit purposes.
        </p>

        <div className="flex flex-wrap gap-2">
          <PolicyLink href={LINKS.terms} testid="terms-link">Terms &amp; Conditions</PolicyLink>
          <PolicyLink href={LINKS.privacy} testid="privacy-link">Privacy Policy</PolicyLink>
          <PolicyLink href={LINKS.aiDisclaimer} testid="ai-disclaimer-link">AI Disclaimer</PolicyLink>
        </div>

        <label
          className="flex items-start gap-3 p-4 rounded-lg cursor-pointer"
          style={{ background: "var(--vb-bg-2)", border: "1px solid var(--vb-border)" }}
          data-testid="terms-agree-label"
        >
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 shrink-0"
            style={{ accentColor: "var(--vb-text-2)" }}
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            data-testid="terms-agree-checkbox"
          />
          <span className="text-sm" style={{ color: "var(--vb-text-2)" }}>
            I have read and agree to the{" "}
            <a href={LINKS.terms} target="_blank" rel="noopener noreferrer" className="vb-link font-semibold">Terms &amp; Conditions</a>{" "}
            and{" "}
            <a href={LINKS.privacy} target="_blank" rel="noopener noreferrer" className="vb-link font-semibold">Privacy Policy</a>.
          </span>
        </label>
      </div>
    </StepLayout>
  );
}

function PolicyLink({ href, children, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
      style={{ background: "var(--vb-bg-2)", border: "1px solid var(--vb-border)", color: "var(--vb-text-2)" }}
    >
      {children} <ExternalLink size={12} />
    </a>
  );
}
